import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useMatch, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import {
  addDoc,
  collection,
  doc,
  DocumentData,
  getDocs,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { DragEndEvent } from '@dnd-kit/core';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/ReactToastify.css';
import { db } from '../../config/config';
import Header from '../Hub/components/Header';
import PageHeading from '../../components/PageHeading';
import StandaloneBackButton from '../../components/StandaloneBackButton';
import { ROUTES } from '../../constants';
import { posterGrid } from '../../utils/surfaces';
import useHomeScreenApp from '../../utils/useHomeScreenApp';
import ComicFilterBar from './components/ComicFilterBar';
import LibraryView from './components/LibraryView';
import SectionPage from './components/SectionPage';
import ComicCard from './components/ComicCard';
import SeriesDialog from './components/SeriesDialog';
import ComicFormModal from './components/ComicFormModal';
import SeriesFormModal from './components/SeriesFormModal';
import GroupFormModal from './components/GroupFormModal';
import ConfirmDeleteModal from './components/ConfirmDeleteModal';
import ShelfTile, { TileProgress } from './components/ShelfTile';
import {
  COMICS_COLLECTION,
  Comic,
  ComicFormat,
  ComicGroup,
  ComicInput,
  ComicSeries,
  ComicsTab,
  EMPTY_MESSAGES,
  GROUPS_COLLECTION,
  GROUPS_CONTAINER,
  LibraryShelf,
  LibraryTile,
  OwnedFilter,
  ReadFilter,
  ReadStatus,
  SERIES_COLLECTION,
  STANDALONE_KEY,
  SeriesInput,
  SortKey,
  TAB_SORTS,
  UNGROUPED_SERIES_KEY,
  byReadingOrder,
  checklistSize,
  comicTileId,
  firstStarted,
  completeLabel,
  groupDragId,
  isInProgress,
  isRead,
  isStarted,
  isToRead,
  isWantToRead,
  isWishlist,
  isWishlistSeries,
  nextUnread,
  millis,
  seriesProgress,
  seriesTileId,
} from './types';
import { reorder, topOrder } from './ordering';
import { ComicSearchResult, fetchSeriesIssueCovers, trackByForType } from './comicSearch';

const PAGE_TITLE = 'Comic Shelf';

type MenuTarget = { kind: 'comic' | 'series' | 'group'; id: string };

// A change to one doc, applied locally first and then saved in a batch.
interface Write {
  collectionName: string;
  id: string;
  changes: DocumentData;
}

const DEFAULT_SORTS: Record<ComicsTab, SortKey> = {
  library: 'custom',
  reading: 'lastRead',
  toRead: 'added',
  wishlist: 'custom',
};

const patchList = <T extends { id: string }>(list: T[], name: string, writes: Write[]) =>
  list.map((item) => {
    const changes = writes
      .filter((write) => write.collectionName === name && write.id === item.id)
      .reduce((all, write) => ({ ...all, ...write.changes }), {});
    return Object.keys(changes).length ? { ...item, ...changes } : item;
  });

// One identity for the life of the component, always running the latest
// version: memoised cards don't re-render just because a closure is new.
const useStableCallback = <A extends unknown[], R>(fn: (...args: A) => R) => {
  const ref = useRef(fn);
  ref.current = fn;
  return useCallback((...args: A) => ref.current(...args), []);
};

const plural = (count: number, word: string, many = `${word}s`) =>
  `${count} ${count === 1 ? word : many}`;

const Comics = () => {
  useHomeScreenApp('/manifest-comics.json', PAGE_TITLE);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [groups, setGroups] = useState<ComicGroup[]>([]);
  const [series, setSeries] = useState<ComicSeries[]>([]);
  const [comics, setComics] = useState<Comic[]>([]);
  const [loading, setLoading] = useState(true);
  const [isReadOnly, setIsReadOnly] = useState(true);

  const [tab, setTab] = useState<ComicsTab>('library');
  // A shelf section on its own page: /comic-shelf/section/:sectionId
  const sectionId = useMatch(`${ROUTES.comicShelf}/section/:sectionId`)?.params.sectionId ?? null;
  const navigate = useNavigate();
  const location = useLocation();
  // Where the shelf was scrolled to, for coming back from a section
  const shelfScroll = useRef(0);
  const [search, setSearch] = useState('');
  const [format, setFormat] = useState<ComicFormat | 'all'>('all');
  const [publisher, setPublisher] = useState('all');
  const [sorts, setSorts] = useState<Record<ComicsTab, SortKey>>(DEFAULT_SORTS);
  const [readFilter, setReadFilter] = useState<ReadFilter>('all');
  const [ownedFilter, setOwnedFilter] = useState<OwnedFilter>('all');

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuTarget, setMenuTarget] = useState<MenuTarget | null>(null);
  const [menuView, setMenuView] = useState<'main' | 'move'>('main');

  const [comicForm, setComicForm] = useState<{
    open: boolean;
    comic: Comic | null;
    presetSeriesId: string | null;
  }>({ open: false, comic: null, presetSeriesId: null });
  const [seriesForm, setSeriesForm] = useState<{ open: boolean; series: ComicSeries | null }>({
    open: false,
    series: null,
  });
  const [groupForm, setGroupForm] = useState<{ open: boolean; group: ComicGroup | null }>({
    open: false,
    group: null,
  });
  const [deleteTarget, setDeleteTarget] = useState<MenuTarget | null>(null);
  const [openSeriesId, setOpenSeriesId] = useState<string | null>(null);
  // Issue covers looked up per series, by number
  const [issueCovers, setIssueCovers] = useState<Record<string, Map<number, string>>>({});

  /* Anonymous visitors are read-only */
  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), (user) => setIsReadOnly(!user));
    return () => unsub();
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [groupSnap, seriesSnap, comicSnap] = await Promise.all([
          getDocs(collection(db, GROUPS_COLLECTION)),
          getDocs(collection(db, SERIES_COLLECTION)),
          getDocs(collection(db, COMICS_COLLECTION)),
        ]);
        setGroups(groupSnap.docs.map((d) => ({ id: d.id, ...d.data() })) as ComicGroup[]);
        setSeries(
          seriesSnap.docs.map((d) => ({
            id: d.id,
            groupId: null,
            sortOrder: 0,
            ...d.data(),
          })) as ComicSeries[]
        );
        setComics(
          comicSnap.docs.map((d) => ({
            id: d.id,
            seriesId: null,
            groupId: null,
            readingOrder: 0,
            sortOrder: 0,
            readCount: 0,
            favourite: false,
            ...d.data(),
          })) as Comic[]
        );
      } catch (err) {
        console.error(err);
        toast.error('Failed to load your comics');
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  /* ─────────────────────────────
   * Lookups
   * ───────────────────────────── */
  const seriesById = useMemo(() => new Map(series.map((entry) => [entry.id, entry])), [series]);
  const groupsById = useMemo(() => new Map(groups.map((entry) => [entry.id, entry])), [groups]);
  const sortedGroups = useMemo(
    () => [...groups].sort((a, b) => a.sortOrder - b.sortOrder),
    [groups]
  );

  // A comic whose series has gone counts as standalone, so nothing vanishes.
  const seriesOf = (comic: Comic) =>
    comic.seriesId ? seriesById.get(comic.seriesId) ?? null : null;
  const isStandalone = (comic: Comic) => seriesOf(comic) === null;
  const groupIdOf = (groupId: string | null) =>
    groupId && groupsById.has(groupId) ? groupId : null;

  const comicsInSeries = (id: string) =>
    comics.filter((comic) => comic.seriesId === id).sort(byReadingOrder);

  const seriesCover = (entry: ComicSeries) =>
    entry.coverUrl || comicsInSeries(entry.id).find((comic) => comic.coverUrl)?.coverUrl;

  const publishers = useMemo(
    () =>
      Array.from(
        new Set(
          [...comics.map((comic) => comic.publisher), ...series.map((entry) => entry.publisher)]
            .map((name) => name?.trim())
            .filter((name): name is string => Boolean(name))
        )
      ).sort((a, b) => a.localeCompare(b)),
    [comics, series]
  );

  // "Batman (2016) · Vol. 9", "Detective Comics (2016) #934" or "Standalone"
  const subtitleFor = (comic: Comic) => {
    const parent = seriesOf(comic);
    if (!parent) {
      const group = comic.groupId ? groupsById.get(comic.groupId) : undefined;
      return group ? `Standalone · ${group.name}` : 'Standalone';
    }
    const name = `${parent.title}${parent.startYear ? ` (${parent.startYear})` : ''}`;
    if (comic.number == null) return name;
    return comic.format === 'issue' ? `${name} #${comic.number}` : `${name} · Vol. ${comic.number}`;
  };

  /* ─────────────────────────────
   * Filters. Search filters as you type; it's all local data.
   * ───────────────────────────── */
  const query = search.trim().toLowerCase();

  const comicMatches = (comic: Comic, withStatus = true) => {
    const parent = seriesOf(comic);
    if (withStatus && readFilter !== 'all' && isRead(comic) !== (readFilter === 'read')) return false;
    if (withStatus && ownedFilter !== 'all' && comic.owned !== (ownedFilter === 'owned')) return false;
    if (format !== 'all' && comic.format !== format) return false;
    if (publisher !== 'all' && (comic.publisher || parent?.publisher) !== publisher) return false;
    if (!query) return true;
    return [comic.title, parent?.title, comic.publisher, ...(comic.writers ?? []), ...(comic.artists ?? [])]
      .filter(Boolean)
      .some((text) => (text as string).toLowerCase().includes(query));
  };

  // A series counts as read once started, unread until finished, owned
  // when any of it is, and not owned while any of it isn't.
  const seriesMatches = (entry: ComicSeries, withStatus = true) => {
    const inSeries = comicsInSeries(entry.id);
    if (withStatus && (readFilter !== 'all' || ownedFilter !== 'all')) {
      const { read, owned, total } = seriesProgress(entry, comics);
      if (readFilter === 'read' && read === 0) return false;
      if (readFilter === 'unread' && total > 0 && read >= total) return false;
      if (ownedFilter === 'owned' && owned === 0) return false;
      if (ownedFilter === 'notOwned' && total > 0 && owned >= total) return false;
    }
    if (format !== 'all' && !inSeries.some((comic) => comic.format === format)) return false;
    if (
      publisher !== 'all' &&
      entry.publisher !== publisher &&
      !inSeries.some((comic) => comic.publisher === publisher)
    ) {
      return false;
    }
    if (!query) return true;
    return (
      [entry.title, entry.publisher].some((text) => text?.toLowerCase().includes(query)) ||
      inSeries.some((comic) => [comic.title, ...(comic.writers ?? []), ...(comic.artists ?? [])]
        .some((text) => text.toLowerCase().includes(query)))
    );
  };

  const isFiltering =
    Boolean(query) ||
    format !== 'all' ||
    publisher !== 'all' ||
    readFilter !== 'all' ||
    ownedFilter !== 'all';
  // Section pages are Library shelves, so they use its sort
  const sortTab: ComicsTab = sectionId ? 'library' : tab;
  const sort = sorts[sortTab];

  const sortComics = (list: Comic[], key: SortKey) =>
    [...list].sort((a, b) => {
      if (key === 'title') return a.title.localeCompare(b.title);
      if (key === 'added') return millis(b.createdAt) - millis(a.createdAt);
      if (key === 'lastRead') return millis(b.lastReadAt) - millis(a.lastReadAt);
      return (a.wantToReadOrder ?? 0) - (b.wantToReadOrder ?? 0) || millis(b.createdAt) - millis(a.createdAt);
    });

  /* ─────────────────────────────
   * Library shelves
   * ───────────────────────────── */

  // Every cover on a shelf, filtered or not, in My order.
  const allTilesFor = (key: string): LibraryTile[] => {
    const seriesTiles: LibraryTile[] =
      key === STANDALONE_KEY
        ? []
        : series
            .filter((entry) =>
              key === UNGROUPED_SERIES_KEY ? groupIdOf(entry.groupId) === null : entry.groupId === key
            )
            .map((entry) => ({
              kind: 'series',
              id: seriesTileId(entry.id),
              series: entry,
              sortOrder: entry.sortOrder,
            }));
    const comicTiles: LibraryTile[] =
      key === UNGROUPED_SERIES_KEY
        ? []
        : comics
            .filter(
              (comic) =>
                isStandalone(comic) &&
                (key === STANDALONE_KEY ? groupIdOf(comic.groupId) === null : comic.groupId === key)
            )
            .map((comic) => ({
              kind: 'comic',
              id: comicTileId(comic.id),
              comic,
              sortOrder: comic.sortOrder,
            }));
    return [...seriesTiles, ...comicTiles].sort((a, b) => a.sortOrder - b.sortOrder);
  };

  const tileTitle = (tile: LibraryTile) =>
    tile.kind === 'series' ? tile.series.title : tile.comic.title;
  const tileCreated = (tile: LibraryTile) =>
    millis(tile.kind === 'series' ? tile.series.createdAt : tile.comic.createdAt);

  const tileProgress = (tile: LibraryTile): TileProgress | undefined =>
    tile.kind === 'series' ? seriesProgress(tile.series, comics) : undefined;

  const tileCover = (tile: LibraryTile) =>
    tile.kind === 'series' ? seriesCover(tile.series) : tile.comic.coverUrl;

  const shelfMeta = (tiles: LibraryTile[]) => {
    const seriesCount = tiles.filter((tile) => tile.kind === 'series').length;
    const standaloneCount = tiles.length - seriesCount;
    let read = 0;
    let total = 0;
    tiles.forEach((tile) => {
      if (tile.kind === 'series') {
        const progress = seriesProgress(tile.series, comics);
        read += progress.read;
        total += progress.total;
      } else {
        total += 1;
        if (isRead(tile.comic)) read += 1;
      }
    });
    return [
      seriesCount ? plural(seriesCount, 'series', 'series') : '',
      standaloneCount ? plural(standaloneCount, 'standalone') : '',
      total ? `${read} of ${total} read` : '',
    ]
      .filter(Boolean)
      .join(' · ');
  };

  const shelves = useMemo((): LibraryShelf[] => {
    const visibleTiles = (key: string) => {
      const tiles = allTilesFor(key).filter((tile) =>
        tile.kind === 'series' ? seriesMatches(tile.series) : comicMatches(tile.comic)
      );
      if (sort === 'title') return tiles.sort((a, b) => tileTitle(a).localeCompare(tileTitle(b)));
      if (sort === 'added') return tiles.sort((a, b) => tileCreated(b) - tileCreated(a));
      return tiles;
    };

    const result: LibraryShelf[] = sortedGroups.map((group) => {
      const tiles = visibleTiles(group.id);
      return {
        key: group.id,
        group,
        title: group.name,
        meta: shelfMeta(allTilesFor(group.id)),
        tiles,
      };
    });

    const ungrouped = visibleTiles(UNGROUPED_SERIES_KEY);
    if (ungrouped.length) {
      result.push({
        key: UNGROUPED_SERIES_KEY,
        group: null,
        title: 'Ungrouped series',
        meta: shelfMeta(ungrouped),
        tiles: ungrouped,
      });
    }

    const standalone = visibleTiles(STANDALONE_KEY);
    if (standalone.length) {
      result.push({
        key: STANDALONE_KEY,
        group: null,
        title: 'Standalone',
        meta: 'Not in a series or a group',
        tiles: standalone,
      });
    }

    // Filtering hides groups with nothing to show
    return isFiltering ? result.filter((shelf) => shelf.tiles.length > 0) : result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, series, comics, query, format, publisher, sort, readFilter, ownedFilter]);

  /* ─────────────────────────────
   * Section pages
   * ───────────────────────────── */
  const sectionTitles: Record<string, string> = {
    [UNGROUPED_SERIES_KEY]: 'Ungrouped series',
    [STANDALONE_KEY]: 'Standalone',
  };
  const sectionExists = (id: string) => groupsById.has(id) || id in sectionTitles;
  const sectionHref = (shelf: LibraryShelf) => `${ROUTES.comicShelf}/section/${shelf.key}`;

  // Filters can empty a section, which drops it from shelves; it still gets a page
  const sectionShelf: LibraryShelf | null = sectionId
    ? shelves.find((shelf) => shelf.key === sectionId) ?? {
        key: sectionId,
        group: groupsById.get(sectionId) ?? null,
        title: groupsById.get(sectionId)?.name ?? sectionTitles[sectionId] ?? '',
        meta: '',
        tiles: [],
      }
    : null;

  // Back goes back when we came from the shelf, so the iPhone gesture and
  // the button agree; a link opened cold goes to the shelf instead.
  const leaveSection = () => {
    if ((location.state as { fromShelf?: boolean } | null)?.fromShelf) navigate(-1);
    else navigate(ROUTES.comicShelf);
  };

  // A section opens at its top; the shelf comes back where it was
  useEffect(() => {
    if (sectionId) {
      window.scrollTo(0, 0);
      return;
    }
    const top = shelfScroll.current;
    requestAnimationFrame(() => window.scrollTo(0, top));
  }, [sectionId]);

  /* ─────────────────────────────
   * Card tabs
   * ───────────────────────────── */
  const toReadCount = useMemo(() => comics.filter(isToRead).length, [comics]);

  // On the go: series started but not finished, and standalones marked
  // started. Most recently touched first.
  const lastTouched = (comic: Comic) => Math.max(millis(comic.lastReadAt), millis(comic.startedAt));
  const lastReadIn = (entry: ComicSeries) => Math.max(0, ...comicsInSeries(entry.id).map(lastTouched));
  const isReadingStandalone = (comic: Comic) => isStarted(comic) && isStandalone(comic);

  const readingTiles = useMemo(() => {
    const tiles: LibraryTile[] = [
      ...series
        .filter((entry) => isInProgress(entry, comics) && seriesMatches(entry, false))
        .map((entry): LibraryTile => ({ kind: 'series', id: seriesTileId(entry.id), series: entry, sortOrder: 0 })),
      ...comics
        .filter((comic) => isReadingStandalone(comic) && comicMatches(comic, false))
        .map((comic): LibraryTile => ({ kind: 'comic', id: comicTileId(comic.id), comic, sortOrder: 0 })),
    ];
    const touched = (tile: LibraryTile) =>
      tile.kind === 'series' ? lastReadIn(tile.series) : lastTouched(tile.comic);
    return sort === 'title'
      ? tiles.sort((a, b) => tileTitle(a).localeCompare(tileTitle(b)))
      : tiles.sort((a, b) => touched(b) - touched(a));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comics, series, query, format, publisher, sort]);
  const readingCount = useMemo(
    () =>
      series.filter((entry) => isInProgress(entry, comics)).length +
      comics.filter(isReadingStandalone).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [comics, series]
  );

  // The count sits on the tile's status line, so this is just where I am
  const readingDetail = (entry: ComicSeries) => {
    const prefix = entry.trackBy === 'issue' ? '#' : 'Vol. ';
    const started = firstStarted(entry, comics);
    return started != null ? `Reading: ${prefix}${started}` : `Next: ${prefix}${nextUnread(entry, comics)}`;
  };

  const visibleComics = useMemo(() => {
    if (tab !== 'toRead') return [];
    return sortComics(comics.filter(isToRead).filter((comic) => comicMatches(comic)), sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comics, series, tab, query, format, publisher, sort, readFilter, ownedFilter]);

  // The Wishlist mixes series and comics in one order. Comics keep their own
  // ids (drag ids match ComicCard's); series take the "s:" tile id.
  const allWishlistTiles = (): LibraryTile[] => [
    ...series.filter(isWishlistSeries).map((entry): LibraryTile => ({
      kind: 'series',
      id: seriesTileId(entry.id),
      series: entry,
      sortOrder: entry.wantToReadOrder ?? 0,
    })),
    ...comics.filter(isWishlist).map((comic): LibraryTile => ({
      kind: 'comic',
      id: comic.id,
      comic,
      sortOrder: comic.wantToReadOrder ?? 0,
    })),
  ];
  const wishlistAdded = (tile: LibraryTile) =>
    tile.kind === 'series'
      ? millis(tile.series.wishlistedAt) || millis(tile.series.createdAt)
      : millis(tile.comic.createdAt);
  const sortWishlist = (tiles: LibraryTile[], key: SortKey) =>
    [...tiles].sort((a, b) => {
      if (key === 'title') return tileTitle(a).localeCompare(tileTitle(b));
      if (key === 'added') return wishlistAdded(b) - wishlistAdded(a);
      return a.sortOrder - b.sortOrder || wishlistAdded(b) - wishlistAdded(a);
    });

  const wishlistTiles = useMemo(() => {
    if (tab !== 'wishlist') return [];
    const visible = allWishlistTiles().filter((tile) =>
      tile.kind === 'series' ? seriesMatches(tile.series) : comicMatches(tile.comic)
    );
    return sortWishlist(visible, sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comics, series, tab, query, format, publisher, sort, readFilter, ownedFilter]);

  const canDrag = !isReadOnly && sort === 'custom';

  /* ─────────────────────────────
   * Saving. Changes show straight away, then save in one batch; a failed
   * save puts everything back.
   * ───────────────────────────── */
  const saveWrites = async (writes: Write[], errorMessage: string, successMessage?: string) => {
    if (writes.length === 0) return true;
    const previous = { groups, series, comics };
    setGroups((prev) => patchList(prev, GROUPS_COLLECTION, writes));
    setSeries((prev) => patchList(prev, SERIES_COLLECTION, writes));
    setComics((prev) => patchList(prev, COMICS_COLLECTION, writes));

    try {
      const batch = writeBatch(db);
      writes.forEach(({ collectionName, id, changes }) => {
        batch.update(doc(db, collectionName, id), changes);
      });
      await batch.commit();
      if (successMessage) toast.success(successMessage);
      return true;
    } catch (err) {
      console.error(err);
      setGroups(previous.groups);
      setSeries(previous.series);
      setComics(previous.comics);
      toast.error(errorMessage);
      return false;
    }
  };

  const comicWrite = (id: string, changes: DocumentData): Write => ({
    collectionName: COMICS_COLLECTION,
    id,
    changes,
  });

  const shelfKeyFor = (groupId: string | null) => groupIdOf(groupId) ?? STANDALONE_KEY;
  const topOfShelf = (key: string) => topOrder(allTilesFor(key).map((tile) => tile.sortOrder));
  // Wishlisted comics and series share one order
  const topOfWantToRead = () =>
    topOrder([
      ...comics.filter(isWantToRead).map((comic) => comic.wantToReadOrder),
      ...series.filter(isWishlistSeries).map((entry) => entry.wantToReadOrder),
    ]);

  const seriesWrite = (id: string, changes: DocumentData): Write => ({
    collectionName: SERIES_COLLECTION,
    id,
    changes,
  });

  /* ─────────────────────────────
   * Wishlist. Series come off it once I start reading them, with an Undo.
   * ───────────────────────────── */
  const wishlistOn = (): DocumentData => ({
    wishlisted: true,
    wishlistedAt: Timestamp.now(),
    wantToReadOrder: topOfWantToRead(),
  });
  const WISHLIST_OFF = { wishlisted: false, wishlistedAt: null, wantToReadOrder: null };

  const toastUndo = (message: string, undo: Write[]) =>
    toast.info(({ closeToast }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1 }}>{message}</Box>
        <Button
          size="small"
          onClick={() => {
            saveWrites(undo, 'Failed to undo');
            closeToast?.();
          }}
        >
          Undo
        </Button>
      </Box>
    ));

  const STARTED_MESSAGE = "Removed from Wishlist — you've started reading";

  const leaveWishlist = async (entry?: ComicSeries | null) => {
    if (!entry?.wishlisted) return;
    const restore = {
      wishlisted: true,
      wishlistedAt: entry.wishlistedAt ?? null,
      wantToReadOrder: entry.wantToReadOrder ?? null,
    };
    if (await saveWrites([seriesWrite(entry.id, WISHLIST_OFF)], 'Failed to update the Wishlist')) {
      toastUndo(STARTED_MESSAGE, [seriesWrite(entry.id, restore)]);
    }
  };

  const handleToggleSeriesWishlist = (entry: ComicSeries) =>
    saveWrites(
      [seriesWrite(entry.id, entry.wishlisted ? WISHLIST_OFF : wishlistOn())],
      'Failed to update the Wishlist',
      entry.wishlisted ? `Removed "${entry.title}" from the Wishlist` : `Added "${entry.title}" to the Wishlist`
    );

  /* ─────────────────────────────
   * Comics
   * ───────────────────────────── */
  const markAsReadChanges = (comic: Comic) => ({
    readStatus: 'read' as ReadStatus,
    readCount: (comic.readCount ?? 0) + 1,
    lastReadAt: Timestamp.now(),
  });

  const handleMarkAsRead = async (comic: Comic) => {
    const parent = seriesOf(comic);
    const wasWanted = isWantToRead(comic);
    const quiet = wasWanted || Boolean(parent?.wishlisted);
    const saved = await saveWrites(
      [comicWrite(comic.id, markAsReadChanges(comic))],
      'Failed to mark as read',
      quiet ? undefined : comic.readStatus === 'read' ? `Read "${comic.title}" again` : `Marked "${comic.title}" as read`
    );
    if (!saved) return saved;
    if (parent?.wishlisted) await leaveWishlist(parent);
    else if (wasWanted) {
      toastUndo(STARTED_MESSAGE, [
        comicWrite(comic.id, {
          readStatus: 'wantToRead',
          wantToReadOrder: comic.wantToReadOrder ?? null,
          readCount: comic.readCount ?? 0,
          lastReadAt: comic.lastReadAt ?? null,
        }),
      ]);
    }
    return saved;
  };

  // Starting one counts as starting to read: a wishlisted series (or the
  // comic itself) comes off the Wishlist, with an Undo
  const handleMarkStarted = async (comic: Comic) => {
    const parent = seriesOf(comic);
    const wasWanted = isWantToRead(comic);
    const saved = await saveWrites(
      [comicWrite(comic.id, { readStatus: 'started', startedAt: Timestamp.now() })],
      'Failed to update comic'
    );
    if (!saved) return saved;
    if (parent?.wishlisted) await leaveWishlist(parent);
    else if (wasWanted) {
      toastUndo(STARTED_MESSAGE, [
        comicWrite(comic.id, {
          readStatus: 'wantToRead',
          wantToReadOrder: comic.wantToReadOrder ?? null,
          startedAt: comic.startedAt ?? null,
        }),
      ]);
    }
    return saved;
  };

  // Each tap moves it on: not read → started → read → not read
  const handleToggleRead = (comic: Comic) =>
    isRead(comic)
      ? saveWrites([comicWrite(comic.id, { readStatus: 'unread' })], 'Failed to update comic')
      : isStarted(comic)
      ? handleMarkAsRead(comic)
      : handleMarkStarted(comic);

  // New comics in one batch, shown straight away; a failed save takes them back out.
  const createComics = async (entries: Omit<Comic, 'id'>[], errorMessage: string) => {
    const refs = entries.map(() => doc(collection(db, COMICS_COLLECTION)));
    const created = entries.map((entry, index) => ({ ...entry, id: refs[index].id }));
    setComics((prev) => [...prev, ...created]);
    try {
      const batch = writeBatch(db);
      entries.forEach((entry, index) => batch.set(refs[index], entry));
      await batch.commit();
      return true;
    } catch (err) {
      console.error(err);
      const ids = new Set(created.map((comic) => comic.id));
      setComics((prev) => prev.filter((comic) => !ids.has(comic.id)));
      toast.error(errorMessage);
      return false;
    }
  };

  // A checklist number that isn't in the library yet, added as read (or,
  // from a single tap, as started)
  const checklistComic = (
    entry: ComicSeries,
    number: number,
    status: 'read' | 'started' = 'read'
  ): Omit<Comic, 'id'> => {
    const isIssue = entry.trackBy === 'issue';
    const read = status === 'read';
    return {
      title: isIssue ? `${entry.title} #${number}` : `${entry.title} Vol. ${number}`,
      format: isIssue ? 'issue' : 'trade',
      seriesId: entry.id,
      groupId: null,
      number,
      readingOrder: number,
      sortOrder: 0,
      owned: false,
      readStatus: status,
      readCount: read ? 1 : 0,
      lastReadAt: read ? Timestamp.now() : null,
      startedAt: read ? null : Timestamp.now(),
      favourite: false,
      wantToReadOrder: null,
      coverUrl: issueCovers[entry.id]?.get(number) ?? '',
      writers: [],
      artists: [],
      publisher: entry.publisher ?? '',
      notes: '',
      externalId: null,
      createdAt: Timestamp.now(),
    };
  };

  const comicNumbered = (entry: ComicSeries, number: number) =>
    comicsInSeries(entry.id).find((comic) => comic.number === number);

  const handleToggleNumber = async (entry: ComicSeries, number: number) => {
    const existing = comicNumbered(entry, number);
    if (!existing) {
      if (await createComics([checklistComic(entry, number, 'started')], 'Failed to mark as started')) {
        await leaveWishlist(entry);
      }
    } else if (isRead(existing) && entry.complete) {
      // Un-ticking one means the series isn't complete any more
      await saveWrites(
        [comicWrite(existing.id, { readStatus: 'unread' }), seriesWrite(entry.id, { complete: false })],
        'Failed to update comic'
      );
    } else {
      await handleToggleRead(existing);
    }
  };

  // What marking 1 to upTo read takes: updates to logged ones, new docs for the rest
  const rangeChanges = (entry: ComicSeries, upTo: number) => {
    const writes: Write[] = [];
    const missing: Omit<Comic, 'id'>[] = [];
    for (let number = 1; number <= upTo; number += 1) {
      const existing = comicNumbered(entry, number);
      if (!existing) missing.push(checklistComic(entry, number));
      else if (!isRead(existing)) writes.push(comicWrite(existing.id, markAsReadChanges(existing)));
    }
    return { writes, missing };
  };

  const saveRange = (writes: Write[], missing: Omit<Comic, 'id'>[]) =>
    Promise.all([
      saveWrites(writes, 'Failed to mark as read'),
      missing.length ? createComics(missing, 'Failed to add the missing ones') : true,
    ]).then(([updated, created]) => updated && created);

  const handleMarkRange = async (entry: ComicSeries, upTo: number) => {
    const { writes, missing } = rangeChanges(entry, upTo);
    if (writes.length === 0 && missing.length === 0) {
      toast.info(`Already read up to ${upTo}`);
      return;
    }
    if (await saveRange(writes, missing)) {
      const prefix = entry.trackBy === 'issue' ? '#' : 'Vol. ';
      toast.success(`Marked ${prefix}1–${upTo} of ${entry.title} as read`);
      await leaveWishlist(entry);
    }
  };

  // Complete (or caught up) ticks off the whole checklist; undoing it keeps the ticks
  const handleToggleComplete = async (entry: ComicSeries) => {
    const label = completeLabel(entry);
    if (entry.complete) {
      saveWrites([seriesWrite(entry.id, { complete: false })], 'Failed to update series');
      return;
    }
    const { writes, missing } = rangeChanges(entry, checklistSize(entry, comics));
    if (await saveRange([...writes, seriesWrite(entry.id, { complete: true })], missing)) {
      toast.success(`${entry.title}: ${label.toLowerCase()}`);
      await leaveWishlist(entry);
    }
  };

  const handleSetReadStatus = (comic: Comic, status: ReadStatus) => {
    if (status === 'read') return handleMarkAsRead(comic);
    return saveWrites(
      [
        comicWrite(
          comic.id,
          status === 'wantToRead'
            ? { readStatus: status, wantToReadOrder: topOfWantToRead() }
            : { readStatus: status }
        ),
      ],
      'Failed to update comic'
    );
  };

  const handleToggleOwned = (comic: Comic) =>
    saveWrites([comicWrite(comic.id, { owned: !comic.owned })], 'Failed to update comic');

  const handleToggleFavourite = (comic: Comic) =>
    saveWrites([comicWrite(comic.id, { favourite: !comic.favourite })], 'Failed to update comic');

  const handleSaveComic = async (input: ComicInput) => {
    const existing = comicForm.comic;

    const duplicate = comics.find(
      (comic) =>
        comic.id !== existing?.id &&
        comic.seriesId === input.seriesId &&
        comic.title.toLowerCase() === input.title.toLowerCase()
    );
    if (duplicate) {
      toast.error(`"${input.title}" is already in your comics`);
      return false;
    }

    const nextReadingOrder = (seriesId: string | null) =>
      seriesId
        ? Math.max(-1, ...comics.filter((comic) => comic.seriesId === seriesId).map((comic) => comic.readingOrder)) + 1
        : 0;

    if (existing) {
      const changes: DocumentData = { ...input };
      if (input.readStatus === 'read' && existing.readStatus !== 'read') {
        Object.assign(changes, markAsReadChanges(existing));
      }
      if (input.readStatus === 'started' && existing.readStatus !== 'started') {
        changes.startedAt = Timestamp.now();
      }
      if (input.readStatus === 'wantToRead' && existing.readStatus !== 'wantToRead') {
        changes.wantToReadOrder = topOfWantToRead();
      }
      if (input.seriesId !== existing.seriesId) {
        changes.readingOrder = input.number ?? nextReadingOrder(input.seriesId);
      } else if (input.number !== existing.number && existing.readingOrder === existing.number) {
        // Still in number order, so keep following the number
        changes.readingOrder = input.number ?? existing.readingOrder;
      }
      if (!input.seriesId && (existing.seriesId || input.groupId !== existing.groupId)) {
        changes.sortOrder = topOfShelf(shelfKeyFor(input.groupId));
      }
      const saved = await saveWrites([comicWrite(existing.id, changes)], 'Failed to save comic', `Saved "${input.title}"`);
      const begun = (status: ReadStatus) => status === 'read' || status === 'started';
      if (saved && begun(input.readStatus) && !begun(existing.readStatus) && input.seriesId) {
        await leaveWishlist(seriesById.get(input.seriesId));
      }
      return saved;
    }

    try {
      const entry: Omit<Comic, 'id'> = {
        ...input,
        readingOrder: input.number ?? nextReadingOrder(input.seriesId),
        sortOrder: input.seriesId ? 0 : topOfShelf(shelfKeyFor(input.groupId)),
        readCount: input.readStatus === 'read' ? 1 : 0,
        lastReadAt: null,
        startedAt: input.readStatus === 'started' ? Timestamp.now() : null,
        favourite: false,
        wantToReadOrder: input.readStatus === 'wantToRead' ? topOfWantToRead() : null,
        createdAt: Timestamp.now(),
      };
      const ref = await addDoc(collection(db, COMICS_COLLECTION), entry);
      setComics((prev) => [...prev, { ...entry, id: ref.id }]);
      toast.success(`Added "${input.title}"`);
      if ((input.readStatus === 'read' || input.readStatus === 'started') && input.seriesId) {
        await leaveWishlist(seriesById.get(input.seriesId));
      }
      return true;
    } catch (err) {
      console.error(err);
      toast.error('Failed to add comic');
      return false;
    }
  };

  /* ─────────────────────────────
   * Series and groups
   * ───────────────────────────── */
  const handleSaveSeries = async (input: SeriesInput) => {
    const existing = seriesForm.series;
    const shelfKey = groupIdOf(input.groupId) ?? UNGROUPED_SERIES_KEY;

    if (existing) {
      const changes: DocumentData = { ...input };
      if (input.groupId !== existing.groupId) changes.sortOrder = topOfShelf(shelfKey);
      if (input.wishlisted && !existing.wishlisted) Object.assign(changes, wishlistOn());
      if (!input.wishlisted && existing.wishlisted) Object.assign(changes, WISHLIST_OFF);
      return saveWrites(
        [{ collectionName: SERIES_COLLECTION, id: existing.id, changes }],
        'Failed to save series',
        `Saved "${input.title}"`
      );
    }

    try {
      const entry: Omit<ComicSeries, 'id'> = {
        ...input,
        ...(input.wishlisted ? wishlistOn() : WISHLIST_OFF),
        sortOrder: topOfShelf(shelfKey),
        createdAt: Timestamp.now(),
      };
      const ref = await addDoc(collection(db, SERIES_COLLECTION), entry);
      setSeries((prev) => [...prev, { ...entry, id: ref.id }]);
      toast.success(`Added "${input.title}"`);
      return true;
    } catch (err) {
      console.error(err);
      toast.error('Failed to add series');
      return false;
    }
  };

  // Picking a whole run in the Add Comic search adds it as an ungrouped
  // series. Metron's type says how to track it; Comic Vine only counts
  // issues, so its total is left for me to set.
  const handleCreateSeriesFromSearch = async (
    result: ComicSearchResult,
    extras: { coverUrl: string; publisher: string }
  ) => {
    const trackBy = result.seriesType ? trackByForType(result.seriesType) : 'volume';
    const ongoing = result.source === 'metron' && trackBy === 'issue' && !result.ended;
    try {
      const entry: Omit<ComicSeries, 'id'> = {
        title: result.title,
        startYear: result.year,
        volumeNumber: null,
        publisher: extras.publisher,
        groupId: null,
        trackBy,
        totalCount: result.source === 'metron' && !ongoing ? result.count || null : null,
        issueCount: result.count || null,
        ongoing,
        coverUrl: extras.coverUrl,
        externalId: { source: result.source, id: result.id },
        sortOrder: topOfShelf(UNGROUPED_SERIES_KEY),
        createdAt: Timestamp.now(),
      };
      const ref = await addDoc(collection(db, SERIES_COLLECTION), entry);
      setSeries((prev) => [...prev, { ...entry, id: ref.id }]);
      toast.success(`Added the series "${result.title}". Edit it to put it in a group.`);
      return ref.id;
    } catch (err) {
      console.error(err);
      toast.error('Failed to add series');
      return null;
    }
  };

  const handleSaveGroup = async (name: string) => {
    const existing = groupForm.group;
    if (groups.some((group) => group.id !== existing?.id && group.name.toLowerCase() === name.toLowerCase())) {
      toast.error(`There's already a "${name}" group`);
      return false;
    }

    if (existing) {
      return saveWrites(
        [{ collectionName: GROUPS_COLLECTION, id: existing.id, changes: { name } }],
        'Failed to rename group'
      );
    }

    try {
      const entry: Omit<ComicGroup, 'id'> = {
        name,
        sortOrder: topOrder(groups.map((group) => group.sortOrder)),
        createdAt: Timestamp.now(),
      };
      const ref = await addDoc(collection(db, GROUPS_COLLECTION), entry);
      setGroups((prev) => [...prev, { ...entry, id: ref.id }]);
      toast.success(`Added the "${name}" group`);
      return true;
    } catch (err) {
      console.error(err);
      toast.error('Failed to add group');
      return false;
    }
  };

  const handleMoveToGroup = (target: MenuTarget, groupId: string | null) => {
    const shelfKey =
      target.kind === 'series'
        ? groupId ?? UNGROUPED_SERIES_KEY
        : groupId ?? STANDALONE_KEY;
    const groupName = groupId ? groupsById.get(groupId)?.name : null;
    saveWrites(
      [
        {
          collectionName: target.kind === 'series' ? SERIES_COLLECTION : COMICS_COLLECTION,
          id: target.id,
          changes: { groupId, sortOrder: topOfShelf(shelfKey) },
        },
      ],
      'Failed to move',
      groupName ? `Moved to ${groupName}` : 'Removed from its group'
    );
  };

  /* ─────────────────────────────
   * Deleting
   * ───────────────────────────── */
  const deleteName = (target: MenuTarget | null) => {
    if (!target) return '';
    if (target.kind === 'comic') return comics.find((c) => c.id === target.id)?.title ?? '';
    if (target.kind === 'series') return seriesById.get(target.id)?.title ?? '';
    return groupsById.get(target.id)?.name ?? '';
  };

  const deleteDetail = (target: MenuTarget | null) => {
    if (!target || target.kind === 'comic') return undefined;
    if (target.kind === 'series') {
      const count = comicsInSeries(target.id).length;
      return count ? `Its ${plural(count, 'comic')} will be deleted too.` : undefined;
    }
    return 'Its series and graphic novels are kept and move out of the group.';
  };

  const handleDelete = async () => {
    const target = deleteTarget;
    if (!target) return;
    setDeleteTarget(null);
    const name = deleteName(target);

    try {
      const batch = writeBatch(db);
      if (target.kind === 'comic') {
        batch.delete(doc(db, COMICS_COLLECTION, target.id));
      } else if (target.kind === 'series') {
        batch.delete(doc(db, SERIES_COLLECTION, target.id));
        comicsInSeries(target.id).forEach((comic) => batch.delete(doc(db, COMICS_COLLECTION, comic.id)));
      } else {
        batch.delete(doc(db, GROUPS_COLLECTION, target.id));
        series
          .filter((entry) => entry.groupId === target.id)
          .forEach((entry) => batch.update(doc(db, SERIES_COLLECTION, entry.id), { groupId: null }));
        comics
          .filter((comic) => comic.groupId === target.id)
          .forEach((comic) => batch.update(doc(db, COMICS_COLLECTION, comic.id), { groupId: null }));
      }
      await batch.commit();

      if (target.kind === 'comic') {
        setComics((prev) => prev.filter((comic) => comic.id !== target.id));
      } else if (target.kind === 'series') {
        setSeries((prev) => prev.filter((entry) => entry.id !== target.id));
        setComics((prev) => prev.filter((comic) => comic.seriesId !== target.id));
        if (openSeriesId === target.id) setOpenSeriesId(null);
      } else {
        setGroups((prev) => prev.filter((group) => group.id !== target.id));
        setSeries((prev) =>
          prev.map((entry) => (entry.groupId === target.id ? { ...entry, groupId: null } : entry))
        );
        setComics((prev) =>
          prev.map((comic) => (comic.groupId === target.id ? { ...comic, groupId: null } : comic))
        );
      }
      toast.success(`Deleted "${name}"`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete');
    }
  };

  /* ─────────────────────────────
   * Drag and drop
   * ───────────────────────────── */
  const handleLibraryDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const container = active.data.current?.container as string | undefined;

    if (container === GROUPS_CONTAINER) {
      const updates = reorder(
        sortedGroups,
        (group) => groupDragId(group.id),
        (group) => group.sortOrder,
        String(active.id),
        String(over.id)
      );
      saveWrites(
        updates.map(({ item, order }) => ({
          collectionName: GROUPS_COLLECTION,
          id: item.id,
          changes: { sortOrder: order },
        })),
        'Failed to save the new order'
      );
      return;
    }

    if (!container) return;
    const updates = reorder(
      allTilesFor(container),
      (tile) => tile.id,
      (tile) => tile.sortOrder,
      String(active.id),
      String(over.id)
    );
    saveWrites(
      updates.map(({ item, order }) =>
        item.kind === 'series'
          ? { collectionName: SERIES_COLLECTION, id: item.series.id, changes: { sortOrder: order } }
          : comicWrite(item.comic.id, { sortOrder: order })
      ),
      'Failed to save the new order'
    );
  };

  const handleQueueDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const updates = reorder(
      sortWishlist(allWishlistTiles(), 'custom'),
      (tile) => tile.id,
      (tile) => (tile.kind === 'series' ? tile.series.wantToReadOrder : tile.comic.wantToReadOrder),
      String(active.id),
      String(over.id)
    );
    saveWrites(
      updates.map(({ item, order }) =>
        item.kind === 'series'
          ? seriesWrite(item.series.id, { wantToReadOrder: order })
          : comicWrite(item.comic.id, { wantToReadOrder: order })
      ),
      'Failed to save the new order'
    );
  };

  // Opening a series looks up its issue covers once, then fills in any
  // comic still without one (checklist ticks start without a cover).
  useEffect(() => {
    const entry = openSeriesId ? seriesById.get(openSeriesId) : undefined;
    if (!entry?.externalId || isReadOnly || issueCovers[entry.id]) return;
    let cancelled = false;
    fetchSeriesIssueCovers(entry.externalId)
      .then((covers) => {
        if (cancelled) return;
        setIssueCovers((prev) => ({ ...prev, [entry.id]: covers }));
        const writes = comicsInSeries(entry.id)
          .filter((comic) => !comic.coverUrl && comic.number != null && covers.has(comic.number))
          .map((comic) => comicWrite(comic.id, { coverUrl: covers.get(comic.number as number) }));
        saveWrites(writes, 'Failed to save issue covers');
      })
      .catch((err) => console.error('Issue covers lookup failed', err));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSeriesId, isReadOnly]);

  /* ─────────────────────────────
   * Opening things
   * ───────────────────────────── */
  const openComic = (comic: Comic) => {
    if (seriesOf(comic)) setOpenSeriesId(comic.seriesId);
    else if (!isReadOnly) setComicForm({ open: true, comic, presetSeriesId: null });
  };

  const handleOpenTile = (tile: LibraryTile) => {
    if (tile.kind === 'series') setOpenSeriesId(tile.series.id);
    else openComic(tile.comic);
  };

  /* ─────────────────────────────
   * Menu: one for comics, series and groups
   * ───────────────────────────── */
  const openMenu = (e: React.MouseEvent<HTMLButtonElement>, target: MenuTarget) => {
    setAnchorEl(e.currentTarget);
    setMenuTarget(target);
    setMenuView('main');
  };

  const closeMenu = () => setAnchorEl(null);

  // Card callbacks with fixed identities, so memoised cards skip re-renders
  const stableOpenTile = useStableCallback(handleOpenTile);
  const stableOpenComic = useStableCallback(openComic);
  const stableTileMenu = useStableCallback(
    (e: React.MouseEvent<HTMLButtonElement>, tile: LibraryTile) =>
      openMenu(e, { kind: tile.kind, id: tile.kind === 'series' ? tile.series.id : tile.comic.id })
  );
  const stableComicMenu = useStableCallback((e: React.MouseEvent<HTMLButtonElement>, comic: Comic) =>
    openMenu(e, { kind: 'comic', id: comic.id })
  );
  const stableToggleRead = useStableCallback(handleToggleRead);
  const stableToggleOwned = useStableCallback(handleToggleOwned);
  const stableToggleFavourite = useStableCallback(handleToggleFavourite);

  const menuComic =
    menuTarget?.kind === 'comic' ? comics.find((comic) => comic.id === menuTarget.id) ?? null : null;
  const menuSeries = menuTarget?.kind === 'series' ? seriesById.get(menuTarget.id) ?? null : null;
  const menuGroup = menuTarget?.kind === 'group' ? groupsById.get(menuTarget.id) ?? null : null;
  const menuCurrentGroup = menuSeries
    ? groupIdOf(menuSeries.groupId)
    : menuComic
    ? groupIdOf(menuComic.groupId)
    : null;

  // Runs a menu action and closes the menu
  const act = (action: () => void) => () => {
    closeMenu();
    action();
  };

  const comicMenuItems = (comic: Comic) => [
    <MenuItem key="edit" onClick={act(() => setComicForm({ open: true, comic, presetSeriesId: null }))}>
      Edit
    </MenuItem>,
    comic.readStatus === 'read' && (
      <MenuItem key="again" onClick={act(() => handleMarkAsRead(comic))}>
        Read again
      </MenuItem>
    ),
    (comic.readStatus === 'unread' || comic.readStatus === 'wantToRead') && (
      <MenuItem
        key="want"
        onClick={act(() =>
          handleSetReadStatus(comic, comic.readStatus === 'wantToRead' ? 'unread' : 'wantToRead')
        )}
      >
        {comic.readStatus === 'wantToRead' ? 'Remove from Wishlist' : 'Add to Wishlist'}
      </MenuItem>
    ),
    <MenuItem key="owned" onClick={act(() => handleToggleOwned(comic))}>
      {comic.owned ? 'Mark as not owned' : 'Mark as owned'}
    </MenuItem>,
    <MenuItem key="fav" onClick={act(() => handleToggleFavourite(comic))}>
      {comic.favourite ? 'Remove from favourites' : 'Add to favourites'}
    </MenuItem>,
    isStandalone(comic) && (
      <MenuItem key="move" onClick={() => setMenuView('move')}>
        Move to group…
      </MenuItem>
    ),
    <Divider key="divider" />,
    <MenuItem key="delete" sx={{ color: 'error.main' }} onClick={act(() => setDeleteTarget({ kind: 'comic', id: comic.id }))}>
      Delete
    </MenuItem>,
  ];

  const seriesMenuItems = (entry: ComicSeries) => [
    <MenuItem key="open" onClick={act(() => setOpenSeriesId(entry.id))}>
      Open
    </MenuItem>,
    <MenuItem key="edit" onClick={act(() => setSeriesForm({ open: true, series: entry }))}>
      Edit series
    </MenuItem>,
    <MenuItem key="want" onClick={act(() => handleToggleSeriesWishlist(entry))}>
      {entry.wishlisted ? 'Remove from Wishlist' : 'Add to Wishlist'}
    </MenuItem>,
    <MenuItem
      key="add"
      onClick={act(() => setComicForm({ open: true, comic: null, presetSeriesId: entry.id }))}
    >
      Add comic to series
    </MenuItem>,
    <MenuItem key="move" onClick={() => setMenuView('move')}>
      Move to group…
    </MenuItem>,
    <Divider key="divider" />,
    <MenuItem key="delete" sx={{ color: 'error.main' }} onClick={act(() => setDeleteTarget({ kind: 'series', id: entry.id }))}>
      Delete
    </MenuItem>,
  ];

  const groupMenuItems = (group: ComicGroup) => [
    <MenuItem key="rename" onClick={act(() => setGroupForm({ open: true, group }))}>
      Rename
    </MenuItem>,
    <MenuItem key="delete" sx={{ color: 'error.main' }} onClick={act(() => setDeleteTarget({ kind: 'group', id: group.id }))}>
      Delete
    </MenuItem>,
  ];

  const moveMenuItems = () =>
    [{ id: null as string | null, name: 'No group' }, ...sortedGroups].map((option) => (
      <MenuItem
        key={option.id ?? 'none'}
        selected={option.id === menuCurrentGroup}
        onClick={act(() => {
          if (menuTarget && option.id !== menuCurrentGroup) handleMoveToGroup(menuTarget, option.id);
        })}
      >
        <ListItemIcon sx={{ visibility: option.id === menuCurrentGroup ? 'visible' : 'hidden' }}>
          <CheckIcon fontSize="small" />
        </ListItemIcon>
        {option.name}
      </MenuItem>
    ));

  const menuItems =
    menuView === 'move'
      ? moveMenuItems()
      : menuComic
      ? comicMenuItems(menuComic)
      : menuSeries
      ? seriesMenuItems(menuSeries)
      : menuGroup
      ? groupMenuItems(menuGroup)
      : [];

  const openSeries = openSeriesId ? seriesById.get(openSeriesId) ?? null : null;

  const libraryEmpty = groups.length === 0 && series.length === 0 && comics.length === 0;
  const emptyMessage = query
    ? `Nothing matches "${search.trim()}"`
    : isFiltering
    ? 'Nothing matches these filters.'
    : EMPTY_MESSAGES[tab];

  const filterBar = (
    <ComicFilterBar
      isReadOnly={isReadOnly}
      search={search}
      onSearch={setSearch}
      format={format}
      onFormat={setFormat}
      publisher={publisher}
      publishers={publishers}
      onPublisher={setPublisher}
      sort={sort}
      sortOptions={TAB_SORTS[sortTab]}
      onSort={(value) => setSorts((prev) => ({ ...prev, [sortTab]: value }))}
      showStatusFilters={sortTab !== 'reading'}
      readFilter={readFilter}
      onReadFilter={setReadFilter}
      ownedFilter={ownedFilter}
      onOwnedFilter={setOwnedFilter}
      onClear={() => {
        setSearch('');
        setFormat('all');
        setPublisher('all');
        setReadFilter('all');
        setOwnedFilter('all');
      }}
      onNewGroup={() => setGroupForm({ open: true, group: null })}
      onNewSeries={() => setSeriesForm({ open: true, series: null })}
      onAddComic={() => setComicForm({ open: true, comic: null, presetSeriesId: null })}
    />
  );

  const spinner = (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
      <CircularProgress color="primary" />
    </Box>
  );

  const libraryProps = {
    isReadOnly,
    isMobile,
    canDrag,
    tileCover,
    tileProgress,
    onOpenTile: stableOpenTile,
    onTileMenu: stableTileMenu,
    onGroupMenu: (e: React.MouseEvent<HTMLButtonElement>, group: ComicGroup) =>
      openMenu(e, { kind: 'group', id: group.id }),
    onToggleRead: stableToggleRead,
    onDragEnd: handleLibraryDragEnd,
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Header />

      <Container maxWidth="xl" sx={{ py: 4, flex: 1 }}>
        {sectionId && sectionShelf ? (
          !loading && !sectionExists(sectionId) ? (
            <Navigate to={ROUTES.comicShelf} replace />
          ) : (
            <SectionPage
              title={sectionShelf.title}
              meta={shelfMeta(allTilesFor(sectionShelf.key))}
              onBack={leaveSection}
            >
              {filterBar}
              {loading ? (
                spinner
              ) : sectionShelf.tiles.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography color="text.secondary">
                    {isFiltering ? emptyMessage : 'Nothing in this section yet.'}
                  </Typography>
                </Box>
              ) : (
                <LibraryView {...libraryProps} shelves={[sectionShelf]} layout="grid" hideHeadings />
              )}
            </SectionPage>
          )
        ) : (
          <>
            <StandaloneBackButton />
            <PageHeading>{PAGE_TITLE}</PageHeading>

            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Tabs
                value={tab}
                onChange={(_, value: ComicsTab) => setTab(value)}
                textColor="primary"
                indicatorColor="primary"
                variant={isMobile ? 'fullWidth' : 'standard'}
                sx={{
                  width: isMobile ? '100%' : 'auto',
                  '& .MuiTab-root': isMobile ? { minWidth: 0, px: 0.5, whiteSpace: 'nowrap' } : {},
                }}
              >
                <Tab label="Library" value="library" />
                {/* Counts don't fit four tabs across a phone */}
                <Tab label={isMobile ? 'Reading' : `Reading (${readingCount})`} value="reading" />
                <Tab label={isMobile ? 'To Read' : `To Read (${toReadCount})`} value="toRead" />
                <Tab label="Wishlist" value="wishlist" />
              </Tabs>
            </Box>

            {filterBar}

            {loading ? (
              spinner
            ) : tab === 'library' ? (
              libraryEmpty || shelves.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography color="text.secondary">
                    {libraryEmpty ? EMPTY_MESSAGES.library : emptyMessage}
                  </Typography>
                </Box>
              ) : (
                <LibraryView
                  {...libraryProps}
                  shelves={shelves}
                  sectionHref={sectionHref}
                  onOpenSection={() => {
                    shelfScroll.current = window.scrollY;
                  }}
                />
              )
            ) : tab === 'reading' ? (
              readingTiles.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography color="text.secondary">{emptyMessage}</Typography>
                </Box>
              ) : (
                <Box sx={posterGrid}>
                      {readingTiles.map((tile) => (
                        <ShelfTile
                          key={tile.id}
                          tile={tile}
                          container="reading"
                          coverUrl={tileCover(tile)}
                          progress={tileProgress(tile)}
                          detail={tile.kind === 'series' ? readingDetail(tile.series) : undefined}
                          isReadOnly={isReadOnly}
                          draggable={false}
                          onOpen={stableOpenTile}
                          onMenuClick={stableTileMenu}
                          onToggleRead={stableToggleRead}
                        />
                      ))}
                    </Box>
              )
            ) : (tab === 'wishlist' ? wishlistTiles.length : visibleComics.length) === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography color="text.secondary">{emptyMessage}</Typography>
              </Box>
            ) : tab === 'wishlist' ? (
              <Box sx={posterGrid}>
                    {wishlistTiles.map((tile) =>
                      tile.kind === 'series' ? (
                        <ShelfTile
                          key={tile.id}
                          tile={tile}
                          container="wishlist"
                          coverUrl={seriesCover(tile.series)}
                          progress={seriesProgress(tile.series, comics)}
                          isReadOnly={isReadOnly}
                          draggable={canDrag}
                          onOpen={stableOpenTile}
                          onMenuClick={stableTileMenu}
                        />
                      ) : (
                        <ComicCard
                          key={tile.id}
                          comic={tile.comic}
                          subtitle={subtitleFor(tile.comic)}
                          isReadOnly={isReadOnly}
                          draggable={canDrag}
                          onOpen={stableOpenComic}
                          onMenuClick={stableComicMenu}
                          onToggleRead={stableToggleRead}
                          onToggleOwned={stableToggleOwned}
                          onToggleFavourite={stableToggleFavourite}
                        />
                      )
                    )}
                  </Box>
            ) : (
              <Box sx={posterGrid}>
                    {visibleComics.map((comic) => (
                      <ComicCard
                        key={comic.id}
                        comic={comic}
                        subtitle={subtitleFor(comic)}
                        isReadOnly={isReadOnly}
                        draggable={false}
                        onOpen={stableOpenComic}
                        onMenuClick={stableComicMenu}
                        onToggleRead={stableToggleRead}
                        onToggleOwned={stableToggleOwned}
                        onToggleFavourite={stableToggleFavourite}
                      />
                    ))}
                  </Box>
            )}

            {!loading && tab === 'toRead' && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
                Showing {plural(visibleComics.length, 'comic')}
              </Typography>
            )}
            {!loading && tab === 'wishlist' && wishlistTiles.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
                Showing{' '}
                {[
                  [wishlistTiles.filter((tile) => tile.kind === 'series').length, 'series', 'series'] as const,
                  [wishlistTiles.filter((tile) => tile.kind === 'comic').length, 'comic', 'comics'] as const,
                ]
                  .filter(([count]) => count > 0)
                  .map(([count, one, many]) => plural(count, one, many))
                  .join(' · ')}
              </Typography>
            )}
          </>
        )}
      </Container>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        {menuItems}
      </Menu>

      <SeriesDialog
        series={openSeries}
        comics={comics}
        coverUrl={openSeries ? seriesCover(openSeries) : undefined}
        groupName={openSeries?.groupId ? groupsById.get(openSeries.groupId)?.name : undefined}
        isReadOnly={isReadOnly}
        onClose={() => setOpenSeriesId(null)}
        onEditSeries={(entry) => setSeriesForm({ open: true, series: entry })}
        onToggleComplete={handleToggleComplete}
        onToggleWishlist={handleToggleSeriesWishlist}
        onMenuClick={stableComicMenu}
        onToggleRead={stableToggleRead}
        onToggleOwned={stableToggleOwned}
        onToggleFavourite={stableToggleFavourite}
        onToggleNumber={handleToggleNumber}
        onMarkRange={handleMarkRange}
      />

      <ComicFormModal
        open={comicForm.open}
        comic={comicForm.comic}
        presetSeriesId={comicForm.presetSeriesId}
        series={series}
        groups={groups}
        onClose={() => setComicForm((prev) => ({ ...prev, open: false }))}
        onSave={handleSaveComic}
        onCreateSeries={handleCreateSeriesFromSearch}
        onOpenSeries={(id) => setOpenSeriesId(id)}
      />

      <SeriesFormModal
        open={seriesForm.open}
        series={seriesForm.series}
        groups={groups}
        onClose={() => setSeriesForm((prev) => ({ ...prev, open: false }))}
        onSave={handleSaveSeries}
      />

      <GroupFormModal
        open={groupForm.open}
        group={groupForm.group}
        onClose={() => setGroupForm((prev) => ({ ...prev, open: false }))}
        onSave={handleSaveGroup}
      />

      <ConfirmDeleteModal
        open={deleteTarget !== null}
        kind={deleteTarget?.kind === 'comic' ? 'Comic' : deleteTarget?.kind === 'series' ? 'Series' : 'Group'}
        name={deleteName(deleteTarget)}
        detail={deleteDetail(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />

      <ToastContainer />
    </Box>
  );
};

export default Comics;
